import React, { useEffect, useRef, useState } from 'react';
import {
  Achievement,
  computeAgentAchievements,
} from '../../utils/agentAchievements';
import './AgentAchievements.scss';

interface AgentAchievementsProps {
  receiptCount: number;
  chains: string[];
  /** ADR-012 endpoint-verification count. Optional so the profile
   *  page can pass it once the backend hydrates the field; treated
   *  as 0 in the meantime, leaving the verified-endpoint chips
   *  visible-but-unearned. */
  verifiedEndpointCount?: number;
  /** Extra class on the wrapper for spacing overrides at call
   *  sites (mirror of AgentCapabilityChips pattern). */
  className?: string;
}

/**
 * Renders the full agent achievement set — earned chips in their
 * tier color, unearned chips muted so the "what's possible" surface
 * is visible without a separate progress bar.
 *
 * Interaction: chips are <button> elements. Tap / click a chip to
 * reveal its description in an inline panel below the strip. Same
 * behavior on desktop + mobile — the previous native `title`
 * attribute was desktop-hover-only and taps on mobile did nothing.
 * Click-outside + Escape close the panel.
 *
 * Data is derived on the client from the profile response's
 * `receiptCount` + `chains` + `verifiedEndpointCount` fields (no
 * separate fetch). If we ever add achievements that need server-
 * side aggregate math (dispute-clean streaks, top-percentile
 * AgentValue), we can swap this component to accept the pre-
 * computed list without changing its render — see
 * agentAchievements.ts for the extension shape.
 */
export const AgentAchievements: React.FC<AgentAchievementsProps> = ({
  receiptCount,
  chains,
  verifiedEndpointCount,
  className = '',
}) => {
  const achievements = computeAgentAchievements({
    receiptCount,
    chains,
    verifiedEndpointCount,
  });
  const earned = achievements.filter((a) => a.earned).length;
  const total = achievements.length;

  const [openId, setOpenId] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click / Escape. Listener attached only while
  // something is open so the always-on cost stays zero.
  useEffect(() => {
    if (openId === null) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpenId(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenId(null);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [openId]);

  const openAch = openId ? achievements.find((a) => a.id === openId) : null;

  return (
    <div
      ref={wrapperRef}
      className={`agent-achievements ${className}`.trim()}
    >
      <div className="agent-achievements__summary">
        <strong>{earned}</strong> of {total} earned
      </div>
      <ul className="agent-achievements__list">
        {achievements.map((a) => (
          <AchievementChip
            key={a.id}
            achievement={a}
            isOpen={openId === a.id}
            onToggle={() => setOpenId(openId === a.id ? null : a.id)}
          />
        ))}
      </ul>
      {openAch && (
        <div
          className="agent-achievements__details"
          role="region"
          aria-label={`${openAch.label} details`}
        >
          <div className="agent-achievements__details-title">
            {openAch.label}
            <span className="agent-achievements__details-state">
              {openAch.earned ? '· earned' : '· not yet earned'}
            </span>
          </div>
          <p className="agent-achievements__details-desc">
            {openAch.description}
          </p>
        </div>
      )}
    </div>
  );
};

const AchievementChip: React.FC<{
  achievement: Achievement;
  isOpen: boolean;
  onToggle: () => void;
}> = ({ achievement, isOpen, onToggle }) => {
  return (
    <li>
      <button
        type="button"
        className={`agent-achievement-chip agent-achievement-chip--${achievement.tier}${
          achievement.earned ? ' is-earned' : ''
        }${isOpen ? ' is-open' : ''}`}
        aria-expanded={isOpen}
        aria-label={
          achievement.earned
            ? `${achievement.label} (earned) — tap for details`
            : `${achievement.label} (not yet earned) — tap for details`
        }
        onClick={onToggle}
      >
        {achievement.label}
      </button>
    </li>
  );
};

export default AgentAchievements;
